import React, { useState, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { ProjectDetailPageProps, AktiverErtragswert } from '../../../types';
import YieldsSection from './sections/YieldsSection';
import YieldForecastsSection from './sections/YieldForecastsSection';
import { useYieldData } from './hooks/useYieldData';
import { projectsApi } from '../../../services/api/projects';
import { useToast } from '../../../contexts/ToastContext';
import { usePageWarnings } from '../../../contexts/PageWarningsContext';
import PageTemplate from '../../layout/PageTemplate';

interface YieldPageProps extends ProjectDetailPageProps {
  canView: boolean;
  canEdit: boolean;
  /** Callback wenn das Projekt aktualisiert wurde (z.B. nach Änderung des aktiven Ertragswerts) */
  onProjectUpdated?: () => void;
}

/**
 * Yield Page Component
 * Detailansicht der Yield Forecasts eines Windpark-Projekts
 */
const YieldPage: React.FC<YieldPageProps> = ({
  project,
  onBack,
  onTabChange,
  currentTab,
  canView,
  canEdit,
  onProjectUpdated
}) => {
  const {
    activeYieldForecast,
    allYieldForecasts,
    isLoading,
    error,
    updateForecastLocally,
    addForecastLocally,
    removeForecastLocally,
    reloadSingleForecast,
  } = useYieldData({ projectId: project.id });

  const { showSuccess, showError } = useToast();

  // PageWarningsContext für API-basierte Validierungen
  const { setValidationsFromAPI } = usePageWarnings();

  // Lokaler State für den aktiven Ertragswert (optimistic update)
  const [localActivePValue, setLocalActivePValue] = useState<AktiverErtragswert | undefined>(
    project.aktiver_ertragswert || 'P75'
  );
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Handler für die Auswahl eines neuen Ertragswerts
   * - Speichert den neuen Wert im Backend
   * - Das Backend triggert automatisch eine GuV-Neuberechnung im Hintergrund
   * - Validierungen werden über den PageWarningsContext propagiert
   */
  const handlePValueSelect = useCallback(async (pValue: AktiverErtragswert) => {
    if (!canEdit || pValue === localActivePValue) return;

    setIsUpdating(true);

    // Optimistic update
    const previousValue = localActivePValue;
    setLocalActivePValue(pValue);

    try {
      const response = await projectsApi.setAktiverErtragswert(project.id, pValue);

      // Validierungen an den PageWarningsContext weitergeben
      setValidationsFromAPI(response.validations);

      // Feedback mit Hinweis auf eventuelle Warnungen
      if (response.validations.warnings.length > 0) {
        showSuccess(`Gespeichert (${response.validations.warnings.length} Warnung${response.validations.warnings.length > 1 ? 'en' : ''})`);
      } else {
        showSuccess('Gespeichert');
      }

      // Benachrichtige Parent-Komponente über das Update
      onProjectUpdated?.();
    } catch (err) {
      // Rollback bei Fehler
      setLocalActivePValue(previousValue);
      showError('Fehler beim Speichern des Ertragswerts');
    } finally {
      setIsUpdating(false);
    }
  }, [canEdit, localActivePValue, project.id, onProjectUpdated, showSuccess, showError, setValidationsFromAPI]);

  return (
    <PageTemplate isLoading={isLoading} error={error}>
      <YieldForecastsSection
        allYieldForecasts={allYieldForecasts}
        canEdit={canEdit}
        onForecastCreated={addForecastLocally}
        onForecastUpdated={updateForecastLocally}
        onForecastDeleted={removeForecastLocally}
        onDeductionChanged={reloadSingleForecast}
      />
      {/* Erträge Section - nur anzeigen wenn Ertragsprognose vorhanden */}
      {activeYieldForecast && (
        <YieldsSection
          activeYieldForecast={activeYieldForecast}
          activePValue={localActivePValue}
          onPValueSelect={canEdit ? handlePValueSelect : undefined}
        />
      )}
    </PageTemplate>
  );
};

export default YieldPage;

